// Attendance Type ID Constants and Helpers
// Based on core_entity_types table

// Request Types (entity_id: 1001)
export const REQUEST_TYPES = {
  ANNUAL_LEAVE: 100001,
  SICK_LEAVE: 100002,
  UNPAID_LEAVE: 100003,
  WORK_FROM_HOME: 100004,
  COMPASSIONATE_LEAVE: 100005,
  PARENTAL_LEAVE: 100006,
  TRAINING: 100007
};

// Request Statuses (entity_id: 1002)
export const REQUEST_STATUS = {
  PENDING: 100101,
  APPROVED: 100102,
  REJECTED: 100103,
  CANCELLED: 100104,
  WITHDRAWN: 100105
};

// General Statuses (entity_id: 1003)
export const GENERAL_STATUS = {
  ACTIVE: 100201,
  INACTIVE: 100202,
  SUSPENDED: 100203
};

// Request Type Labels
export const REQUEST_TYPE_LABELS = {
  [REQUEST_TYPES.ANNUAL_LEAVE]: 'Annual Leave',
  [REQUEST_TYPES.SICK_LEAVE]: 'Sick Leave',
  [REQUEST_TYPES.UNPAID_LEAVE]: 'Unpaid Leave',
  [REQUEST_TYPES.WORK_FROM_HOME]: 'Work From Home',
  [REQUEST_TYPES.COMPASSIONATE_LEAVE]: 'Compassionate Leave',
  [REQUEST_TYPES.PARENTAL_LEAVE]: 'Parental Leave',
  [REQUEST_TYPES.TRAINING]: 'Training'
};

// Request Status Labels
export const REQUEST_STATUS_LABELS = {
  [REQUEST_STATUS.PENDING]: 'Pending',
  [REQUEST_STATUS.APPROVED]: 'Approved',
  [REQUEST_STATUS.REJECTED]: 'Rejected',
  [REQUEST_STATUS.CANCELLED]: 'Cancelled',
  [REQUEST_STATUS.WITHDRAWN]: 'Withdrawn'
};

// Status Badge Styles
export const STATUS_BADGE_STYLES = {
  [REQUEST_STATUS.PENDING]: 'bg-yellow-100 text-yellow-800',
  [REQUEST_STATUS.APPROVED]: 'bg-green-100 text-green-800',
  [REQUEST_STATUS.REJECTED]: 'bg-red-100 text-red-800',
  [REQUEST_STATUS.CANCELLED]: 'bg-gray-100 text-gray-800',
  [REQUEST_STATUS.WITHDRAWN]: 'bg-gray-100 text-gray-800'
};

// Convert integer type ID to string value for database
export const getLeaveTypeString = (typeId) => {
  const typeMap = {
    [REQUEST_TYPES.ANNUAL_LEAVE]: 'annual_leave',
    [REQUEST_TYPES.SICK_LEAVE]: 'sick_leave',
    [REQUEST_TYPES.UNPAID_LEAVE]: 'unpaid_leave',
    [REQUEST_TYPES.WORK_FROM_HOME]: 'work_from_home',
    [REQUEST_TYPES.COMPASSIONATE_LEAVE]: 'compassionate_leave',
    [REQUEST_TYPES.PARENTAL_LEAVE]: 'parental_leave',
    [REQUEST_TYPES.TRAINING]: 'training'
  };
  return typeMap[typeId] || null;
};

// Convert string value from database to integer type ID
export const getLeaveTypeId = (typeString) => {
  const stringMap = {
    'annual_leave': REQUEST_TYPES.ANNUAL_LEAVE,
    'sick_leave': REQUEST_TYPES.SICK_LEAVE,
    'unpaid_leave': REQUEST_TYPES.UNPAID_LEAVE,
    'work_from_home': REQUEST_TYPES.WORK_FROM_HOME,
    'compassionate_leave': REQUEST_TYPES.COMPASSIONATE_LEAVE,
    'parental_leave': REQUEST_TYPES.PARENTAL_LEAVE,
    'training': REQUEST_TYPES.TRAINING
  };
  return stringMap[typeString] || null;
};

// Convert integer status ID to string value for database
export const getStatusString = (statusId) => {
  const statusMap = {
    [REQUEST_STATUS.PENDING]: 'pending',
    [REQUEST_STATUS.APPROVED]: 'approved',
    [REQUEST_STATUS.REJECTED]: 'rejected',
    [REQUEST_STATUS.CANCELLED]: 'cancelled',
    [REQUEST_STATUS.WITHDRAWN]: 'withdrawn'
  };
  return statusMap[statusId] || null;
};

// Convert string value from database to integer status ID
export const getStatusId = (statusString) => {
  const stringMap = {
    'pending': REQUEST_STATUS.PENDING,
    'approved': REQUEST_STATUS.APPROVED,
    'rejected': REQUEST_STATUS.REJECTED,
    'cancelled': REQUEST_STATUS.CANCELLED,
    'withdrawn': REQUEST_STATUS.WITHDRAWN
  };
  return stringMap[statusString] || null;
};

// Helper Functions
export const getRequestTypeLabel = (typeIdOrString) => {
  // Handle both integer IDs and string values
  if (typeof typeIdOrString === 'string') {
    const typeId = getLeaveTypeId(typeIdOrString);
    // If it maps to a known system type, return the label
    if (typeId) {
      return REQUEST_TYPE_LABELS[typeId];
    }
    // Otherwise, it's a custom leave type - return as-is (already in display format)
    return typeIdOrString;
  }
  return REQUEST_TYPE_LABELS[typeIdOrString] || 'Unknown';
};

export const getRequestStatusLabel = (statusIdOrString) => {
  // Handle both integer IDs and string values
  if (typeof statusIdOrString === 'string') {
    statusIdOrString = getStatusId(statusIdOrString);
  }
  return REQUEST_STATUS_LABELS[statusIdOrString] || 'Unknown';
};

export const getStatusBadgeStyle = (statusIdOrString) => {
  // Handle both integer IDs and string values
  if (typeof statusIdOrString === 'string') {
    statusIdOrString = getStatusId(statusIdOrString);
  }
  return STATUS_BADGE_STYLES[statusIdOrString] || 'bg-gray-100 text-gray-800';
};

// Request types for form dropdown
export const LEAVE_TYPE_OPTIONS = [
  { value: REQUEST_TYPES.ANNUAL_LEAVE, label: 'Annual Leave', chargeable: true },
  { value: REQUEST_TYPES.SICK_LEAVE, label: 'Sick Leave', chargeable: false },
  { value: REQUEST_TYPES.UNPAID_LEAVE, label: 'Unpaid Leave', chargeable: false },
  { value: REQUEST_TYPES.WORK_FROM_HOME, label: 'Work From Home', chargeable: false },
  { value: REQUEST_TYPES.COMPASSIONATE_LEAVE, label: 'Compassionate Leave', chargeable: false },
  { value: REQUEST_TYPES.PARENTAL_LEAVE, label: 'Parental Leave', chargeable: false },
  { value: REQUEST_TYPES.TRAINING, label: 'Training', chargeable: false }
];

// Helper to check if status matches (handles both string and integer formats)
export const isStatus = (statusValue, targetStatusId) => {
  if (typeof statusValue === 'string') {
    return getStatusId(statusValue) === targetStatusId;
  }
  return statusValue === targetStatusId;
};

// Helper to check if leave type matches (handles both string and integer formats)
export const isLeaveType = (typeValue, targetTypeId) => {
  if (typeof typeValue === 'string') {
    return getLeaveTypeId(typeValue) === targetTypeId;
  }
  return typeValue === targetTypeId;
};
